import { ChangeDetectionStrategy, Component, OnInit, computed, input, output, signal, inject } from '@angular/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { GroupService } from '../../services/group.service';
import { BucketService } from '../../services/bucket.service';
import { MonthService } from '../../services/month.service';
import { AnalyticsService } from '../../services/analytics.service';
import { EVENT_CREATED_BUDGET } from '../../analytics/events';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiCurrencyInputComponent } from '../../ui/currency-input/currency-input.component';
import { UiInputDirective } from '../../ui/input/input.directive';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { CreateBucketRequest } from '../../generated-api/models';
import { Bucket } from '../../types';
import { BucketTypeSelectorComponent } from '../bucket-type-selector/bucket-type-selector.component';
import { DrawerFooterComponent } from '../../drawer/drawer-layout/drawer-footer.component';
import { bucketTypeAmountLabel, bucketTypeGoalLabel, bucketTypeShowsGoal } from '../bucket-type-options';

type BucketDraft = CreateBucketRequest;
type BucketFormRequestState = { status: 'idle' } | { status: 'submitting' };

@Component({
  selector: 'app-add-bucket',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './add-bucket.component.html',
  host: { class: 'flex flex-1 flex-col' },
  imports: [
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiCurrencyInputComponent,
    UiInputDirective,
    UiButtonDirective,
    BucketTypeSelectorComponent,
    DrawerFooterComponent,
  ],
})
export class AddBucketComponent implements OnInit {
  private analytics = inject(AnalyticsService);
  private bucketService = inject(BucketService);
  private groupService = inject(GroupService);
  private month = inject(MonthService);

  private readonly requestState = signal<BucketFormRequestState>({ status: 'idle' });
  readonly selectedType = signal<CreateBucketRequest['type']>('BUDGET');
  readonly createBucketMutation = injectMutation(() => this.bucketService.createBucketMutationOptions());
  readonly categoryPageQuery = injectQuery(() => this.groupService.groupPageQueryOptions());
  bucket: BucketDraft = {
    amount: 0,
    isSetup: false,
    name: '',
    type: 'BUDGET',
    fund: {
      start: this.month.monthYearFromDate(),
      initialAmount: 0,
    },
  };

  readonly groupId = input<string>();
  readonly createdBucket = output<Bucket>();

  readonly categorySelect = computed(() => {
    const categorySelect = (this.categoryPageQuery.data()?.data ?? []).map(category => ({ ...category }));
    const last = categorySelect.pop();
    if (!last) return categorySelect;
    last.id = ' ';
    categorySelect.unshift(last);
    return categorySelect;
  });

  ngOnInit(): void {
    this.groupId() ? (this.bucket.groupId = this.groupId()) : (this.bucket.groupId = ' ');
  }

  get loading(): boolean {
    return this.requestState().status === 'submitting' || this.createBucketMutation.isPending();
  }

  amountLabel(): string {
    return bucketTypeAmountLabel(this.selectedType());
  }

  goalLabel(): string {
    return bucketTypeGoalLabel();
  }

  showsGoal(): boolean {
    return bucketTypeShowsGoal(this.selectedType());
  }

  onTypeChange(type: CreateBucketRequest['type']): void {
    this.bucket.type = type;
    this.selectedType.set(type);
    if (type !== 'SAVINGS') this.bucket.fund.goal = undefined;
  }

  addBucketFormSubmit(form: NgForm): void {
    if (form.invalid) return;
    this.requestState.set({ status: 'submitting' });

    this.createBucketMutation.mutate(this.createBucketPayload(), {
      onSuccess: createdBucket => {
        this.analytics.sendEvent(EVENT_CREATED_BUDGET);
        this.createdBucket.emit(createdBucket);
      },
      onSettled: () => this.requestState.set({ status: 'idle' }),
    });
  }

  private createBucketPayload(): CreateBucketRequest {
    const { groupId, fund, ...bucket } = this.bucket;

    return {
      ...bucket,
      ...(groupId && groupId !== ' ' ? { groupId } : {}),
      fund: {
        ...fund,
        start: this.month.monthYearFromDate(),
      },
      isSetup: true,
    };
  }
}
